'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useRef, useState } from 'react';
import { useMeasure } from 'react-use';

import cx from 'classnames';
import NextImage from 'next/image';
import PropTypes from 'prop-types';

import FavoriteButton from 'components/globals/FavoriteButton';

import EntryLink from 'components/ui/Link/EntryLink';
import SvgIcon from 'components/ui/SvgIcon';
import Text from 'components/ui/Text';

import useIntersection from 'hooks/useIntersection';
import { useTextAutoContrast } from 'hooks/useTextAutoContrast';
import { useTheme } from 'hooks/useTheme';
import { getColor } from 'utils';
import { addProtocol } from 'utils/url';

import styles from './CardProduct.module.scss';

const iconMap = {
    New: 'newPdpRibbonDark',
};

const CardProduct = ({
    __typename,
    priority,
    category,
    textColor,
    color,
    displayName,
    eyebrow,
    image,
    icon,
    slug,
    sys,
    id,
    isInCarousel,
    hoverColorOverride,
}) => {
    color ??= 'blue';
    textColor ??= 'Auto';
    __typename ??= 'Product';
    priority ??= false;

    const itemId = sys?.id || id;

    const hoverBg = hoverColorOverride || color;
    let isDark = useTextAutoContrast(hoverBg) === 'dark';
    if (!hoverColorOverride) {
        if (textColor === 'Light') {
            isDark = false;
        } else if (textColor === 'Dark') {
            isDark = true;
        }
    }

    const ref = useRef(null);
    const [theme] = useTheme();
    const [cardRef, { width }] = useMeasure();

    const [visible, setVisibility] = useState(priority);

    useIntersection(ref, isIntersecting => setVisibility(!!isIntersecting), {
        root: null,
        rootMargin: '0px',
        threshold: 0,
    });

    if (!category) {
        return null;
    }

    return (
        <article
            ref={ref}
            style={{
                '--card-background-color': getColor(color),
                '--card-hover-color': hoverColorOverride
                    ? getColor(hoverColorOverride)
                    : undefined,
                '--card-width': `${width}px`,
            }}
            className={cx(styles.card, styles[`theme--${theme}`], {
                [styles.isInCarousel]: isInCarousel,
            })}
        >
            <div className={styles.favoriteButtonContainer}>
                <FavoriteButton itemType={'product'} itemId={itemId} />
            </div>
            <EntryLink
                entry={{ slug, __typename, category }}
                className={styles.cardLinkWrap}
            >
                <div
                    ref={cardRef}
                    className={cx(styles.cardInner, {
                        [styles[`cardInner--dark-text`]]: isDark, // WCAG Contrast Level AAA large text
                    })}
                >
                    <div className={styles.cardContent}>
                        {iconMap[icon] && (
                            <div className={styles.iconPlacement}>
                                <div className={styles.icon}>
                                    <SvgIcon type={iconMap[icon]} />
                                </div>
                            </div>
                        )}
                        {image?.url && (
                            <div
                                className={cx(styles.imageContainer, {
                                    [styles[`imageContainer--portrait`]]:
                                        image.height > image.width,
                                })}
                            >
                                <figure
                                    className={cx(styles.image, {
                                        [styles.isVisible]: visible,
                                    })}
                                >
                                    <NextImage
                                        src={addProtocol(image.url)}
                                        width={image.width}
                                        height={image.height}
                                        sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                                        alt={
                                            image.description ||
                                            displayName ||
                                            ''
                                        }
                                        priority={priority}
                                    />
                                </figure>
                            </div>
                        )}
                        <div className={styles.textGroup}>
                            <Text.Theme
                                config={{
                                    'makers-reserve': {
                                        baseTheme: 'labelSmallAlt',
                                        themes: {
                                            large: 'labelMediumAlt',
                                        },
                                    },
                                    default: {
                                        baseTheme: 'labelSmall',
                                    },
                                }}
                                className={styles.eyebrow}
                            >
                                {eyebrow}
                            </Text.Theme>
                            <Text.Theme
                                as="h2"
                                config={{
                                    'makers-reserve': {
                                        baseTheme: 'bodyLarge',
                                        themes: {
                                            large: 'bodyLarge',
                                        },
                                    },
                                    default: {
                                        baseTheme: 'bodyLarge',
                                        themes: {
                                            large: 'bodyMedium',
                                            xxLarge: 'bodyLarge',
                                        },
                                    },
                                }}
                                className={styles.cta}
                            >
                                {displayName}
                            </Text.Theme>
                        </div>
                    </div>
                </div>
            </EntryLink>
        </article>
    );
};

CardProduct.propTypes = {
    __typename: PropTypes.string,
    award: PropTypes.object,
    category: PropTypes.object.isRequired,
    textColor: PropTypes.string,
    color: PropTypes.string,
    priority: PropTypes.bool,
    displayName: PropTypes.string,
    eyebrow: PropTypes.string,
    icon: PropTypes.string,
    image: PropTypes.shape({
        description: PropTypes.string,
        height: PropTypes.number.isRequired,
        width: PropTypes.number.isRequired,
        url: PropTypes.string.isRequired,
    }),
    slug: PropTypes.string.isRequired,
    hoverColorOverride: PropTypes.string,
};

export default CardProduct;
